import type { LinkProps as ChakraLinkProps } from '@chakra-ui/react'
import { Link as ChakraLink } from '@chakra-ui/react'
import NextLink, { type LinkProps as NextLinkProps } from 'next/link'

export type GrandLinkProps = Omit<ChakraLinkProps, 'as'> &
  Omit<NextLinkProps, 'as' | 'passHref' | 'legacyBehavior'> & {
    isExternal?: boolean
  }

// React 19 + Chakra v2 break every alternative:
// - `<ChakraLink as={NextLink}>` → SSR/hydration diverge (<a> server vs <div> client).
// - `@chakra-ui/next-js`'s `Link` → same root cause (internally uses chakra.a + `as: NextLink`).
// - `chakra(NextLink)` factory → style props leak as unknown HTML attributes onto the DOM.
//
// passHref + legacyBehavior keeps NextLink as the outer router/prefetch shell
// while ChakraLink natively renders the styled <a>. The Next codemod
// `new-link` strips <a>s INSIDE NextLink — the opposite of what's needed here,
// since the styled <a> from ChakraLink is the whole point. The proper exit is
// migrating to Chakra v3 (where `asChild` replaces `as`); until then, the
// deprecation warning is the lesser evil.
export const GrandLink = ({
  isExternal,
  href,
  prefetch,
  replace,
  scroll,
  shallow,
  locale,
  children,
  ...chakraProps
}: GrandLinkProps) => {
  return (
    <NextLink
      href={href}
      prefetch={prefetch}
      replace={replace}
      scroll={scroll}
      shallow={shallow}
      locale={locale}
      passHref
      legacyBehavior
    >
      <ChakraLink
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        {...chakraProps}
      >
        {children}
      </ChakraLink>
    </NextLink>
  )
}
